import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserIcon, AlertCircleIcon, SearchIcon, ChevronDownIcon, ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { SearchForm } from '../../components/citizen/SearchForm';
import { SearchResults } from '../../components/citizen/SearchResults';
import { SelectedCitizenSummary } from '../../components/citizen/SelectedCitizenSummary';
import { SearchError } from '../../components/citizen/SearchError';
import { PageHeader } from '../../components/citizen/PageHeader';
import { ActionButtons } from '../../components/citizen/ActionButtons';
import { RegistrationForm } from '../../components/citizen/registration/RegistrationForm';
interface Citizen {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  idNumber: string;
  dateOfBirth: string;
  address: string;
  phoneNumber: string;
  email: string;
  gender?: string;
  nationality?: string;
}
// Mock data for demonstration
const mockCitizens: Citizen[] = [{
  id: '1',
  firstName: 'Abebe',
  lastName: 'Kebede',
  name: 'Abebe Kebede',
  idNumber: 'ETH-1234567',
  dateOfBirth: '1985-05-12',
  address: '123 Bole Road, Addis Ababa',
  phoneNumber: '+251 91 234 5678',
  email: 'abebe.kebede@example.com',
  gender: 'Male',
  nationality: 'Ethiopian'
}, {
  id: '2',
  firstName: 'Tigist',
  lastName: 'Haile',
  name: 'Tigist Haile',
  idNumber: 'ETH-7654321',
  dateOfBirth: '1990-10-25',
  address: '456 Churchill Avenue, Addis Ababa',
  phoneNumber: '+251 92 345 6789',
  email: 'tigist.haile@example.com',
  gender: 'Female',
  nationality: 'Ethiopian'
}, {
  id: '3',
  firstName: 'Dawit',
  lastName: 'Mulugeta',
  name: 'Dawit Mulugeta',
  idNumber: 'ETH-8765432',
  dateOfBirth: '1978-03-18',
  address: '789 Mekelle Road, Mekelle',
  phoneNumber: '+251 93 456 7890',
  email: 'dawit.mulugeta@example.com',
  gender: 'Male',
  nationality: 'Ethiopian'
}, {
  id: '4',
  firstName: 'Meseret',
  lastName: 'Tadesse',
  name: 'Meseret Tadesse',
  idNumber: 'ETH-2345678',
  dateOfBirth: '1992-07-30',
  address: '321 Hawassa Street, Hawassa',
  phoneNumber: '+251 94 567 8901',
  email: 'meseret.tadesse@example.com',
  gender: 'Female',
  nationality: 'Ethiopian'
}, {
  id: '5',
  firstName: 'Yonas',
  lastName: 'Tefera',
  name: 'Yonas Tefera',
  idNumber: 'ETH-3456789',
  dateOfBirth: '1982-12-05',
  address: '654 Gondar Road, Gondar',
  phoneNumber: '+251 95 678 9012',
  email: 'yonas.tefera@example.com',
  gender: 'Male',
  nationality: 'Ethiopian'
}];
// Mock nationality data
const nationalityOptions = [{
  code: 'ETH',
  label: 'Ethiopian'
}, {
  code: 'KEN',
  label: 'Kenyan'
}, {
  code: 'SUD',
  label: 'Sudanese'
}, {
  code: 'ERI',
  label: 'Eritrean'
}, {
  code: 'DJI',
  label: 'Djiboutian'
}, {
  code: 'SOM',
  label: 'Somalian'
}, {
  code: 'UGA',
  label: 'Ugandan'
}, {
  code: 'RWA',
  label: 'Rwandan'
}];
interface SearchFormData {
  nationalId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}
interface RegisterFormData {
  nationalId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  email?: string;
  phoneNumber?: string;
  alternativePhone?: string;
  streetAddress?: string;
  city?: string;
  region?: string;
  postalCode?: string;
}
export const IdentifyCitizenPage = () => {
  const {
    serviceId
  } = useParams<{
    serviceId: string;
  }>();
  const navigate = useNavigate();
  // State for search form
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    nationalId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: ''
  });
  // State for registration form
  const [registerFormData, setRegisterFormData] = useState<RegisterFormData>({
    nationalId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nationality: 'ETH'
  });
  // Form validation errors
  const [searchFormErrors, setSearchFormErrors] = useState<Partial<SearchFormData>>({});
  const [registerFormErrors, setRegisterFormErrors] = useState<Partial<RegisterFormData>>({});
  // UI state
  const [searchResults, setSearchResults] = useState<Citizen[]>([]);
  const [selectedCitizen, setSelectedCitizen] = useState<Citizen | null>(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('core');
  // Service name (would come from API in real app)
  const [serviceName, setServiceName] = useState('Service Request');
  // Service catalog data
  const servicesBySection = {
    'Civil Registry Services': [{
      id: 'bd1',
      title: 'Birth Certificate Request',
      description: 'Apply for new or replacement birth certificates',
      category: 'Birth & Death Records',
      estimatedTime: '3-5 business days',
      fee: '$25.00'
    }, {
      id: 'bd2',
      title: 'Death Certificate Request',
      description: 'Request official death certificates',
      category: 'Birth & Death Records',
      estimatedTime: '3-5 business days',
      fee: '$25.00'
    }, {
      id: 'mf1',
      title: 'Marriage License Application',
      description: 'Apply for a marriage license',
      category: 'Marriage & Family',
      estimatedTime: '5-7 business days',
      fee: '$75.00'
    }, {
      id: 'mf2',
      title: 'Marriage Certificate Request',
      description: 'Request official marriage certificates',
      category: 'Marriage & Family',
      estimatedTime: '3-5 business days',
      fee: '$25.00'
    }],
    'Identity Documents': [{
      id: 'id1',
      title: 'Municipal ID Card',
      description: 'Apply for official municipal identification card',
      category: 'Identity Documents',
      estimatedTime: '5-7 business days',
      fee: '$35.00'
    }, {
      id: 'id2',
      title: 'Document Authentication',
      description: 'Authenticate official documents and certificates',
      category: 'Identity Documents',
      estimatedTime: '3-5 business days',
      fee: '$30.00 per document'
    }],
    'Residency Services': [{
      id: 'rs1',
      title: 'Residency Certificate',
      description: 'Apply for official proof of residency',
      category: 'Residency Services',
      estimatedTime: '3-5 business days',
      fee: '$40.00'
    }, {
      id: 'rs2',
      title: 'Address Change Registration',
      description: 'Register change of address',
      category: 'Residency Services',
      estimatedTime: '2-3 business days',
      fee: '$15.00'
    }]
  };
  // Additional state for service catalog
  const [searchQueryServices, setSearchQueryServices] = useState('');
  const [selectedCategoryServices, setSelectedCategoryServices] = useState('all');
  const [expandedSections, setExpandedSections] = useState<string[]>(Object.keys(servicesBySection));
  const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const toggleSection = (section: string) => {
    setExpandedSections(current => current.includes(section) ? current.filter(s => s !== section) : [...current, section]);
  };
  // Filter services based on search query and selected category
  const filteredSections = Object.entries(servicesBySection).reduce((acc, [section, services]) => {
    const filteredServices = services.filter(service => (selectedCategoryServices === 'all' || service.category === selectedCategoryServices) && (service.title.toLowerCase().includes(searchQueryServices.toLowerCase()) || service.description.toLowerCase().includes(searchQueryServices.toLowerCase())));
    if (filteredServices.length > 0) {
      acc[section] = filteredServices;
    }
    return acc;
  }, {} as Record<string, (typeof servicesBySection)[keyof typeof servicesBySection]>);
  const handleServiceSelect = (serviceId: string) => {
    navigate(`/assisted-creation/identify-citizen/${serviceId}`);
  };
  useEffect(() => {
    // In a real app, this would fetch the service name based on serviceId
    if (serviceId === 'lt1') {
      setServiceName('New Land Title Registration');
    } else if (serviceId === 'bd1') {
      setServiceName('Birth Certificate Request');
    }
  }, [serviceId]);
  // Update search form data
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setSearchFormData({
      ...searchFormData,
      [name]: value
    });
    // Clear error for this field if it exists
    if (searchFormErrors[name as keyof SearchFormData]) {
      setSearchFormErrors({
        ...searchFormErrors,
        [name]: undefined
      });
    }
  };
  // Update registration form data
  const handleRegisterInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRegisterFormData({
      ...registerFormData,
      [name]: value
    });
    // Clear error for this field if it exists
    if (registerFormErrors[name as keyof RegisterFormData]) {
      setRegisterFormErrors({
        ...registerFormErrors,
        [name]: undefined
      });
    }
  };
  // Validate search form
  const validateSearchForm = (): boolean => {
    const errors: Partial<SearchFormData> = {};
    let isValid = true;
    // At least one field should be filled
    if (!searchFormData.nationalId && !searchFormData.firstName && !searchFormData.lastName && !searchFormData.dateOfBirth) {
      errors.nationalId = 'Please enter at least one search criteria';
      isValid = false;
    }
    // Validate National ID format if provided
    if (searchFormData.nationalId && !searchFormData.nationalId.match(/^[A-Z]{3}-\d{7}$/)) {
      errors.nationalId = 'National ID should be in format XXX-1234567';
      isValid = false;
    }
    setSearchFormErrors(errors);
    return isValid;
  };
  // Validate registration form
  const validateRegisterForm = (): boolean => {
    const errors: Partial<RegisterFormData> = {};
    let isValid = true;
    // Required fields
    if (!registerFormData.nationalId) {
      errors.nationalId = 'National ID is required';
      isValid = false;
    } else if (!registerFormData.nationalId.match(/^[A-Z]{3}-\d{7}$/)) {
      errors.nationalId = 'National ID should be in format XXX-1234567';
      isValid = false;
    }
    if (!registerFormData.firstName) {
      errors.firstName = 'First name is required';
      isValid = false;
    }
    if (!registerFormData.lastName) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }
    if (!registerFormData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
      isValid = false;
    } else {
      // Check if date is not in the future
      const dob = new Date(registerFormData.dateOfBirth);
      const today = new Date();
      if (dob > today) {
        errors.dateOfBirth = 'Date of birth cannot be in the future';
        isValid = false;
      }
    }
    if (!registerFormData.gender) {
      errors.gender = 'Gender is required';
      isValid = false;
    }
    if (!registerFormData.nationality) {
      errors.nationality = 'Nationality is required';
      isValid = false;
    }
    if (activeTab === 'contact') {
      if (!registerFormData.phoneNumber) {
        errors.phoneNumber = 'Phone number is required';
        isValid = false;
      }
    }
    if (activeTab === 'address') {
      if (!registerFormData.streetAddress) {
        errors.streetAddress = 'Street address is required';
        isValid = false;
      }
      if (!registerFormData.city) {
        errors.city = 'City is required';
        isValid = false;
      }
      if (!registerFormData.region) {
        errors.region = 'Region is required';
        isValid = false;
      }
    }
    setRegisterFormErrors(errors);
    return isValid;
  };
  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSearchForm()) {
      return;
    }
    setIsLoading(true);
    setSearchError(null);
    // Simulate API call
    setTimeout(() => {
      try {
        // Filter citizens based on search criteria
        const results = mockCitizens.filter(citizen => {
          const matchesNationalId = searchFormData.nationalId ? citizen.idNumber.toLowerCase().includes(searchFormData.nationalId.toLowerCase()) : true;
          const matchesFirstName = searchFormData.firstName ? citizen.firstName.toLowerCase().includes(searchFormData.firstName.toLowerCase()) : true;
          const matchesLastName = searchFormData.lastName ? citizen.lastName.toLowerCase().includes(searchFormData.lastName.toLowerCase()) : true;
          const matchesDateOfBirth = searchFormData.dateOfBirth ? citizen.dateOfBirth === searchFormData.dateOfBirth : true;
          return matchesNationalId && matchesFirstName && matchesLastName && matchesDateOfBirth;
        });
        setSearchResults(results);
        setSearchPerformed(true);
        setIsLoading(false);
        // Clear selection if making a new search
        setSelectedCitizen(null);
      } catch (error) {
        setSearchError('An error occurred while searching. Please try again.');
        setIsLoading(false);
      }
    }, 800); // Simulate network delay
  };
  // Handle citizen selection
  const handleSelectCitizen = (citizen: Citizen) => {
    setSelectedCitizen(citizen);
  };
  // Handle continue to form
  const handleContinue = () => {
    if (selectedCitizen) {
      // Navigate to the form filling page with the selected citizen and service
      navigate(`/assisted-creation/form-filling/${serviceId}/${selectedCitizen.id}`);
    }
  };
  // Open the register new citizen form
  const handleOpenRegisterForm = () => {
    // Pre-fill form with search data if available
    setRegisterFormData({
      ...registerFormData,
      nationalId: searchFormData.nationalId || '',
      firstName: searchFormData.firstName || '',
      lastName: searchFormData.lastName || '',
      dateOfBirth: searchFormData.dateOfBirth || ''
    });
    setRegisterFormErrors({});
    setActiveTab('core');
    setShowRegistrationForm(true);
  };
  // Submit registration form
  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRegisterForm()) {
      return;
    }
    setIsLoading(true);
    // Simulate API call to register citizen
    setTimeout(() => {
      // Create a new citizen object
      const newCitizen: Citizen = {
        id: `new-${Date.now()}`,
        firstName: registerFormData.firstName,
        lastName: registerFormData.lastName,
        name: `${registerFormData.firstName} ${registerFormData.lastName}`,
        idNumber: registerFormData.nationalId,
        dateOfBirth: registerFormData.dateOfBirth,
        address: registerFormData.streetAddress ? `${registerFormData.streetAddress}, ${registerFormData.city}, ${registerFormData.region}` : '',
        phoneNumber: registerFormData.phoneNumber || '',
        email: registerFormData.email || '',
        gender: registerFormData.gender,
        nationality: nationalityOptions.find(n => n.code === registerFormData.nationality)?.label
      };
      // In a real app, this would be returned from the API
      setRegisterSuccess(true);
      setSelectedCitizen(newCitizen);
      setIsLoading(false);
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setShowRegistrationForm(false);
        setRegisterSuccess(false);
      }, 1500);
    }, 1000);
  };
  // Go back to previous page
  const handleBack = () => {
    navigate(-1);
  };
  // Format national ID for display (mask all but last 4 digits)
  const formatNationalId = (id: string) => {
    if (!id) return '';
    const parts = id.split('-');
    if (parts.length !== 2) return id;
    const prefix = parts[0];
    const numbers = parts[1];
    if (numbers.length <= 4) return id;
    const maskedNumbers = '*'.repeat(numbers.length - 4) + numbers.slice(-4);
    return `${prefix}-${maskedNumbers}`;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-3364">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3365">
        <div className="mb-8" data-id="element-3366">
          <button onClick={handleBack} className="flex items-center text-blue-600 hover:text-blue-800 mb-4" data-id="element-3367">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3368" />
            <span data-id="element-3369">Back</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3370">
            Civil Registry Service Catalogue
          </h1>
          <p className="text-gray-600" data-id="element-3371">
            Browse and select from our available civil registry services
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200" data-id="element-3372">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-3373">
            <div className="flex-1 relative" data-id="element-3374">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3375" />
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQueryServices} onChange={e => setSearchQueryServices(e.target.value)} data-id="element-3376" />
            </div>
            <div className="w-full md:w-64" data-id="element-3377">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategoryServices} onChange={e => setSelectedCategoryServices(e.target.value)} data-id="element-3378">
                <option value="all" data-id="element-3379">All Categories</option>
                <option value="Birth & Death Records" data-id="element-3380">
                  Birth & Death Records
                </option>
                <option value="Marriage & Family" data-id="element-3381">Marriage & Family</option>
                <option value="Identity Documents" data-id="element-3382">Identity Documents</option>
                <option value="Residency Services" data-id="element-3383">Residency Services</option>
              </select>
            </div>
          </div>
        </div>

        {/* Services List */}
        <div className="space-y-8" data-id="element-3384">
          {Object.keys(filteredSections).length > 0 ? Object.entries(filteredSections).map(([section, services]) => <div key={section} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200" data-id="element-3385">
                <button className="flex items-center justify-between w-full text-left mb-4" onClick={() => toggleSection(section)} data-id="element-3386">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-3387">
                    {section}
                  </h3>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${expandedSections.includes(section) ? 'rotate-180' : ''}`} data-id="element-3388" />
                </button>
                {expandedSections.includes(section) && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3389">
                    {services.map(service => <button key={service.id} className="text-left p-5 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group relative overflow-hidden bg-white flex flex-col" onClick={() => handleServiceSelect(service.id)} data-id="element-3390">
                        <div className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-10 transition-opacity duration-200" onClick={e => {
                e.stopPropagation();
                navigate(`/service-request/${service.id}`);
              }} data-id="element-3391"></div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-blue-700 transition-colors duration-200" data-id="element-3392">
                          {service.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-5 line-clamp-2" data-id="element-3393">
                          {service.description}
                        </p>
                        <div className="mt-auto" data-id="element-3394">
                          <div className="flex items-center justify-between" data-id="element-3395">
                            <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="element-3396">
                              <div className="flex items-center" data-id="element-3397">
                                <span className="text-purple-500 mr-2" data-id="element-3398">⏱</span>
                                <span className="font-medium" data-id="element-3399">
                                  {service.estimatedTime}
                                </span>
                              </div>
                              <div className="flex items-center" data-id="element-3400">
                                <span className="text-amber-500 mr-2" data-id="element-3401">💰</span>
                                <span className="font-medium" data-id="element-3402">
                                  {service.fee}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button className="w-full mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" onClick={e => {
                  e.stopPropagation();
                  // Navigate to the service request page with the service ID
                  navigate(`/service-request/${service.id}`);
                }} data-id="element-3403">
                            Request Document
                            <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-3404" />
                          </button>
                        </div>
                      </button>)}
                  </div>}
              </div>) : <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-200 text-center" data-id="element-3405">
              <p className="text-gray-500" data-id="element-3406">
                No services found matching your criteria.
              </p>
            </div>}
        </div>
      </div>

      {isRequestFormOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-3407">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-3408">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3409">
              <div className="flex items-center justify-between" data-id="element-3410">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-3411">
                  Request Service
                </h3>
                <button onClick={() => setIsRequestFormOpen(false)} className="text-gray-400 hover:text-gray-500" data-id="element-3412">
                  <XCircleIcon className="h-5 w-5" data-id="element-3413" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4" data-id="element-3414">
              <div className="mb-4" data-id="element-3415">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3416">
                  Service Type
                </label>
                <p className="text-sm text-gray-900" data-id="element-3417">
                  {Object.values(servicesBySection).flat().find(s => s.id === selectedServiceId)?.title || 'Selected Service'}
                </p>
              </div>
              <div className="mb-4" data-id="element-3418">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3419">
                  Applicant Name
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Enter applicant name" data-id="element-3420" />
              </div>
              <div className="mb-4" data-id="element-3421">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3422">
                  Contact Phone
                </label>
                <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Enter phone number" data-id="element-3423" />
              </div>
              <div className="mb-4" data-id="element-3424">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3425">
                  Email Address
                </label>
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Enter email address" data-id="element-3426" />
              </div>
              <div className="mb-4" data-id="element-3427">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3428">
                  Additional Notes
                </label>
                <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" rows={3} placeholder="Any specific requirements or information" data-id="element-3429"></textarea>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-3430">
              <button onClick={() => setIsRequestFormOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3431">
                Cancel
              </button>
              <button onClick={() => {
            setIsRequestFormOpen(false);
            handleServiceSelect(selectedServiceId || '');
          }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-3432">
                Submit Request
              </button>
            </div>
          </div>
        </div>}
    </div>;
};